import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkles, ArrowLeft, FileText, TrendingUp, Edit, Save, Menu, X, Settings, LogOut } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const BrokerClientDetail = () => {
  const { clientId } = useParams();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchClientData();
  }, [clientId]);

  const fetchClientData = async () => {
    try {
      const [clientRes, disputesRes] = await Promise.all([
        axios.get(`${API}/broker/clients/${clientId}`),
        axios.get(`${API}/disputes?client_id=${clientId}`)
      ]);
      setClient(clientRes.data);
      setDisputes(disputesRes.data);
      setFormData({
        client_name: clientRes.data.client_name,
        client_phone: clientRes.data.client_phone || '',
        current_score_experian: clientRes.data.current_score_experian || '',
        current_score_equifax: clientRes.data.current_score_equifax || '',
        current_score_transunion: clientRes.data.current_score_transunion || '',
        goal_score: clientRes.data.goal_score || '',
        status: clientRes.data.status,
        notes: clientRes.data.notes || ''
      });
    } catch (error) {
      toast.error('Failed to load client data');
      navigate('/broker/clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const updateData = {
        ...formData,
        current_score_experian: formData.current_score_experian ? parseInt(formData.current_score_experian) : null,
        current_score_equifax: formData.current_score_equifax ? parseInt(formData.current_score_equifax) : null,
        current_score_transunion: formData.current_score_transunion ? parseInt(formData.current_score_transunion) : null,
        goal_score: formData.goal_score ? parseInt(formData.goal_score) : null
      };
      
      await axios.patch(`${API}/broker/clients/${clientId}`, updateData);
      toast.success('Client updated successfully!');
      setEditing(false);
      fetchClientData();
    } catch (error) {
      toast.error('Failed to update client');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-heading">Loading client details...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-heading font-bold text-gradient">DISPUTEKING.AI</span>
              <span className="block text-xs text-slate-600">Broker Portal</span>
            </div>
            
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => navigate('/broker')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Dashboard</button>
              <button onClick={() => navigate('/broker/clients')} className="text-violet-600 font-medium">Clients</button>
              <button onClick={() => navigate('/education')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Resources</button>
              <button onClick={() => navigate('/settings')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">
                <Settings className="w-5 h-5" />
              </button>
              <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" className="font-heading">
                <LogOut className="w-5 h-5 mr-2" />
                Logout
              </Button>
            </div>

            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={() => navigate('/broker/clients')}
            variant="ghost"
            className="rounded-full"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Clients
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Client Info Card */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-3xl p-8 border-2 border-slate-100">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-heading font-bold">Client Information</h2>
                {!editing ? (
                  <Button
                    onClick={() => setEditing(true)}
                    variant="outline"
                    className="rounded-full font-heading"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setEditing(false)}
                      variant="ghost"
                      className="rounded-full"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      className="bg-violet-600 hover:bg-violet-700 text-white rounded-full font-heading"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  {editing ? (
                    <Input
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      className="rounded-xl"
                    />
                  ) : (
                    <div className="text-lg font-medium">{client.client_name}</div>
                  )}
                </div>

                <div>
                  <Label>Email</Label>
                  <div className="text-lg text-slate-600">{client.client_email}</div>
                </div>

                <div>
                  <Label>Phone</Label>
                  {editing ? (
                    <Input
                      value={formData.client_phone}
                      onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                      className="rounded-xl"
                    />
                  ) : (
                    <div className="text-lg text-slate-600">{client.client_phone || 'Not provided'}</div>
                  )}
                </div>

                <div>
                  <Label>Status</Label>
                  {editing ? (
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className={`inline-block px-4 py-1 rounded-full text-sm font-medium ${
                      client.status === 'active' ? 'bg-green-100 text-green-800' :
                      client.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {client.status}
                    </span>
                  )}
                </div>

                <div>
                  <Label>Notes</Label>
                  {editing ? (
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="rounded-xl min-h-[100px]"
                    />
                  ) : (
                    <div className="text-slate-700 whitespace-pre-wrap">{client.notes || 'No notes'}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Disputes */}
            <div className="bg-white rounded-3xl p-8 border-2 border-slate-100">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-heading font-bold">Dispute History</h3>
                <Button
                  onClick={() => navigate(`/broker/disputes/create/${clientId}`)}
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-full font-heading"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Create Dispute
                </Button>
              </div>
              {disputes.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 mb-4">No disputes yet for this client</p>
                  <Button
                    onClick={() => navigate(`/broker/disputes/create/${clientId}`)}
                    variant="outline"
                    className="rounded-full"
                  >
                    Create First Dispute
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {disputes.map((dispute) => (
                    <div key={dispute.id} className="border border-slate-100 rounded-2xl p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-semibold">{dispute.account_name}</h4>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          dispute.status === 'resolved' ? 'bg-green-100 text-green-800' :
                          dispute.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {dispute.status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">{dispute.bureau} • Account #{dispute.account_number}</p>
                      <p className="text-sm text-slate-700 mt-2">{dispute.dispute_reason}</p>
                      <p className="text-xs text-slate-500 mt-2">
                        Created {new Date(dispute.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Credit Scores */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-3xl p-8 text-white">
              <h3 className="text-xl font-heading font-bold mb-6">Credit Scores</h3>
              
              {editing ? (
                <div className="space-y-4">
                  <div>
                    <Label className="text-white">Experian</Label>
                    <Input
                      type="number"
                      value={formData.current_score_experian}
                      onChange={(e) => setFormData({ ...formData, current_score_experian: e.target.value })}
                      className="bg-white/20 border-white/30 text-white rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Equifax</Label>
                    <Input
                      type="number"
                      value={formData.current_score_equifax}
                      onChange={(e) => setFormData({ ...formData, current_score_equifax: e.target.value })}
                      className="bg-white/20 border-white/30 text-white rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-white">TransUnion</Label>
                    <Input
                      type="number"
                      value={formData.current_score_transunion}
                      onChange={(e) => setFormData({ ...formData, current_score_transunion: e.target.value })}
                      className="bg-white/20 border-white/30 text-white rounded-xl"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-violet-100 mb-1">Experian</div>
                    <div className="text-4xl font-heading font-bold">{client.current_score_experian || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-violet-100 mb-1">Equifax</div>
                    <div className="text-4xl font-heading font-bold">{client.current_score_equifax || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-violet-100 mb-1">TransUnion</div>
                    <div className="text-4xl font-heading font-bold">{client.current_score_transunion || 'N/A'}</div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-3xl p-8 border-2 border-slate-100">
              <h3 className="text-lg font-heading font-semibold mb-4">Goal</h3>
              {editing ? (
                <div>
                  <Label>Target Score</Label>
                  <Input
                    type="number"
                    value={formData.goal_score}
                    onChange={(e) => setFormData({ ...formData, goal_score: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  <span className="text-2xl font-heading font-bold text-green-600">
                    {client.goal_score || 'Not set'}
                  </span>
                </div>
              )}
            </div>

            <div className="bg-white rounded-3xl p-8 border-2 border-slate-100">
              <h3 className="text-lg font-heading font-semibold mb-4">Quick Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-600">Total Disputes</span>
                  <span className="font-semibold">{client.total_disputes}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Client Since</span>
                  <span className="font-semibold">{new Date(client.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Last Updated</span>
                  <span className="font-semibold">{new Date(client.last_updated).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrokerClientDetail;
